import { useEffect, useState } from 'react';

/**
 * Scroll-spy for the header nav.
 *
 * Reports which of `ids` is the section the reader is currently looking at, so
 * the matching nav link can show an active state. Returns null when none of them
 * has reached the reading line yet (i.e. while the hero is still filling the
 * screen) or when `enabled` is false — the section links only exist on the home page.
 */
export const useActiveSection = (ids: string[], enabled: boolean = true): string | null => {
  const [active, setActive] = useState<string | null>(null);
  // Join so the effect compares by value; the caller can pass a fresh array each render.
  const key = ids.join('|');

  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return undefined;
    }

    const sectionIds = key.split('|').filter(Boolean);
    let frame = 0;

    const measure = () => {
      frame = 0;
      // The "reading line" sits a third of the way down the viewport: a section
      // counts as current once its top crosses it.
      const line = window.innerHeight * 0.34;
      let current: string | null = null;

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const { top, bottom } = el.getBoundingClientRect();
        if (top <= line && bottom > line) {
          current = id;
          break;
        }
        // Keep the last section that has already scrolled past the line, so gaps
        // between sections do not blank the nav out.
        if (top <= line) current = id;
      }

      // At the very bottom the final section may be too short to reach the line.
      const atBottom =
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        const last = sectionIds
          .map((id) => document.getElementById(id))
          .filter((el): el is HTMLElement => Boolean(el))
          .pop();
        if (last) current = last.id;
      }

      setActive(current);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [key, enabled]);

  return active;
};
